<template>
  <div class="meal-card">
    <button v-if="isDeletable" @click="onDeleteClick" type="button" class="meal-card__delete">
      <img src="@/assets/img/clear-small-gray-icon.svg" alt="">
    </button>
    <template v-if="isStatusMode">
      <app-button
        v-if="isDeletable && meal.is_cancelled"
        @click.native="onCardRestore"
        size="small"
        class="meal-card__delete meal-card__delete--restore"
      >
        Restore
      </app-button>
      <div
        class="meal-card__status"
        :style="{ backgroundColor: !meal.id ? '#77b017' : isChanged && !meal.is_cancelled ? '#ff8426' : meal.is_cancelled ? 'red' : '#35a4ea' }"
      >
        {{ !meal.id ? 'New' : isChanged && !meal.is_cancelled ? 'Changed' : meal.is_cancelled ? 'Cancelled' : 'Original' }}
      </div>
    </template>
    <div class="meal-card__part meal-card__part--top">
      <div class="meal-card__order-number">{{ orderNumber }}.</div>
      <template v-if="isEditable">
        <app-flex-grid
          :rows="[['150px', '130px', '80px', '80px', '80px']]"
          margin-bottom="0"
        >
          <template #col-1-1>
            <div :style="{ backgroundColor: meal.type.color, borderRadius: '6px', padding: '5px 10px', color: '#fff', fontWeight: 500 }">
              {{ meal.type.name }}
            </div>
          </template>
          <template #col-1-2>
            <app-form-field label="Date">
              <app-date-picker
                @input="onFormElementChange($event, 'date')"
                v-model="mealLocal.date"
                placeholder="Date"
              />
            </app-form-field>
          </template>
          <template #col-1-3>
            <app-form-field label="Time">
              <app-input
                @input="onFormElementChange($event, 'time')"
                v-model="mealLocal.time"
                v-mask="'99:99'"
                placeholder="00:00"
                autocomplete="off"
                id="time"
                name="time"
                @paste.native.prevent
              />
            </app-form-field>
          </template>
          <template #col-1-4>
            <app-form-field label="Clients" label-for="add_meal_clients">
              <app-input
                @input="onFormElementChange(+$event, 'clients')"
                v-model.number="mealLocal.clients"
                type="number"
                name="add_meal_clients"
                id="add_meal_clients"
                placeholder=""
              />
            </app-form-field>
          </template>
          <template #col-1-5>
            <app-form-field label="Crew" label-for="add_meal_crew">
              <app-input
                @input="onFormElementChange(+$event, 'crew')"
                v-model.number="mealLocal.crew"
                type="number"
                name="add_meal_crew"
                id="add_meal_crew"
                placeholder=""
              />
            </app-form-field>
          </template>
        </app-flex-grid>
      </template>
      <template v-else>
        <div class="meal-card__name" :style="{ backgroundColor: meal.type.color }">{{ meal.type.name }}</div>
        <div class="meal-card__val">{{ formatDate(meal.date) }} | {{ meal.time }}</div>
        <div class="meal-card__val meal-card__val--bg">Clients: <b>{{ meal.clients }}</b></div>
        <div class="meal-card__val meal-card__val--bg">Crew: <b>{{ meal.crew }}</b></div>
      </template>
    </div>
    <template v-if="!isClientsHide">
      <div v-if="meal.clients_list && meal.clients_list.length && !isSelectVisible" class="meal-card__part meal-card__part--middle">
        <app-cells :indent="false">
          <app-cells v-for="(client, index) in meal.clients_list" :key="index" :indent="false" gap="10px">
            <img v-if="client.age_type" :src="require(`@/assets/img/${client.age_type}-icon.svg`)" alt="">
            <img v-else src="@/assets/img/age-null-icon.svg" alt="">
            <span class="meal-card__client">{{ client.full_name }}</span>
          </app-cells>
        </app-cells>
      </div>
      <div v-if="isSelectVisible" class="meal-card__part meal-card__part--bottom">
        <app-cells v-if="clientsOptions.length === meal.clients" :indent="false" :margin-bottom="10" gap="10px">
          <app-button v-if="meal.clients_list.length !== meal.clients" @click.native="onClickAddAllButton" size="small">
            Add all
          </app-button>
          <app-button v-if="meal.clients_list.length" @click.native="onClickRemoveAllButton" size="small" theme="error">
            Remove all
          </app-button>
        </app-cells>
        <app-select
          @open="onSelectOpen"
          v-model="meal.clients_list"
          :options="clientsOptions"
          :clearable="false"
          placeholder="Selected clients"
          label="full_name"
          :selectable="onCalcPaxLimit"
          :closeOnSelect="false"
          multiple
        >
          <template #custom-option="option">
            <img v-if="option.age_type" :src="require(`@/assets/img/${option.age_type}-icon.svg`)" alt="" class="select__img">
            <img v-else src="@/assets/img/age-null-icon.svg" alt="">
            {{ option.full_name }}
          </template>
        </app-select>
      </div>
    </template>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns'
import isEqual from 'lodash.isequal'

export default {
  name: 'AppMealCard',
  props: {
    meal: {
      type: Object,
      required: true,
    },
    orderNumber: {
      type: Number,
      default: 0
    },
    clientsOptions: {
      type: Array,
      default: () => []
    },
    isDeletable: {
      type: Boolean,
      default: true
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    isSelectVisible: {
      type: Boolean,
      default: false
    },
    isStatusMode: {
      type: Boolean,
      default: false
    },
    isClientsHide: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      mealLocal: {},
      mealOriginal: {},
      clientsLocal: [],
    }
  },
  computed: {
    isChanged() {
      return Boolean(this.meal.id) && !isEqual(this.mealLocal, this.mealOriginal)
    }
  },
  watch: {
    'meal': {
      handler(meal, previousMeal) {
        this.normalizeMealObject(meal)
        // Parent input echoes must not replace the original comparison values.
        if (meal !== previousMeal) this.mealOriginal = { ...this.mealLocal }
      },
      deep: true,
      immediate: true,
    },
    'meal.clients_list': {
      handler(val) {
        this.$emit('changeMealClients', val)
      },
      immediate: true,
    },
  },
  methods: {
    normalizeMealObject(meal) {
      this.mealLocal = {
        date: meal.date,
        time: meal.time,
        clients: meal.clients,
        crew: meal.crew,
      }
    },
    onFormElementChange(value, element) {
      this.$emit('changeFormElement', value, element)
    },
    onCardRestore() {
      this.$emit('cardRestore')
    },
    onClickAddAllButton() {
      this.meal.clients_list = [ ...this.clientsOptions ]
    },
    onClickRemoveAllButton() {
      this.meal.clients_list = []
    },
    onSelectOpen() {
      this.$emit('selectOpen')
    },
    onCalcPaxLimit() {
      return this.meal.clients_list.length < this.meal.clients
    },
    onDeleteClick() {
      if (!this.isStatusMode) this.$emit('cardDelete')
      else {
        if (this.meal.id) {
          this.$emit('cardCancel')
        } else this.$emit('cardDelete')
      }
    },
    formatDate(date) {
      if (!date) return ''
      return format(parseISO(date), 'dd MMM yyyy')
    },
  },
}
</script>

<style scoped lang="sass">
.meal-card
  position: relative
  margin-bottom: 10px
  border: 1px solid #eaeaea
  border-radius: 10px
  background-color: #fff

.meal-card__part
  padding: 12px 30px 12px 12px

.meal-card__part--top
  display: flex
  align-items: center
  gap: 12px

.meal-card__part--middle
  border-top: 1px solid #e7e7e7
  padding: 12px 25px 12px 12px

.meal-card__part--bottom
  border-top: 1px solid #e7e7e7

.meal-card__select-controls
  margin-bottom: 10px

.meal-card__delete
  position: absolute
  top: 2px
  right: 2px
  padding: 10px
  line-height: 0

.meal-card__delete--restore
  top: 5px
  right: 5px
  min-height: 26px

.meal-card__order-number
  display: flex
  align-items: center
  justify-content: center
  flex-shrink: 0
  width: 36px
  height: 35px
  border-radius: 8px
  background-color: #eee
  color: #181818
  font-size: 12px
  font-weight: 600

.meal-card__name
  max-width: max-content
  width: 100%
  border-radius: 6px
  padding: 5px 10px
  color: #fff
  font-weight: 500

.meal-card__val--bg
  max-width: max-content
  width: 100%
  border-radius: 6px
  padding: 5px 10px
  background-color: #eee

.meal-card__client
  color: #5f5f5f
  font-size: 13px
  transform: translateY(1px)

.meal-card__status
  position: absolute
  bottom: 5px
  right: 5px
  padding: 5px 10px 4px
  border-radius: 15px
  font-size: 10px
  color: white
</style>